// src/pages/billing/ConnectOnboarding.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import {
  Landmark,
  Clock,
  Shield,
  Eye,
  ChevronDown,
  ChevronRight,
  CheckCircle,
} from "lucide-react";

export default function ConnectOnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentBusinessId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [activating, setActivating] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/billing/connect/onboarding-info", {
          headers: { "X-Business-Id": currentBusinessId },
        });
        if (!alive) return;
        setInfo(data);
      } catch (e) {
        const detail =
          e?.response?.data?.detail ||
          e?.message ||
          t("billing.connect.loadError");
        setError(detail);
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentBusinessId]);

  const handleActivate = async () => {
    setActivating(true);
    setError("");
    try {
      const returnUrl = `${window.location.origin}/app/settings/payments?stripe_return=1`;
      const refreshUrl = `${window.location.origin}/app/settings/payments?stripe_refresh=1`;

      const { data } = await api.post(
        "/billing/connect/create",
        {
          return_url: returnUrl,
          refresh_url: refreshUrl,
        },
        {
          headers: { "X-Business-Id": currentBusinessId },
        }
      );

      const url = data?.account_link_url;
      if (!url) throw new Error(t("billing.connect.noLinkError"));

      // Redirect la Stripe onboarding
      window.location.href = url;
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.stripe?.[0] ||
        e?.message ||
        t("billing.connect.activateError");
      setError(detail);
      setActivating(false);
    }
  };

  const iconMap = {
    bank: Landmark,
    clock: Clock,
    shield: Shield,
    eye: Eye,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-6">
        <div className="text-white/70">{t("billing.connect.loading")}</div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-6">
        <div className="w-full max-w-xl rounded-2xl border border-red-400/20 bg-red-400/10 p-6">
          <h2 className="text-xl font-semibold text-red-100">{t("billing.connect.errorTitle")}</h2>
          <p className="text-red-100/80 mt-2">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 rounded-xl border border-white/15 hover:bg-white/10"
          >
            {t("billing.connect.back")}
          </button>
        </div>
      </div>
    );
  }

  // If already connected
  if (info?.already_connected && info?.onboarding_completed) {
    return (
      <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-6">
        <div className="w-full max-w-xl rounded-2xl border border-green-400/20 bg-green-400/10 p-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-400 text-2xl" />
            <h2 className="text-xl font-semibold text-green-100">
              {t("billing.connect.alreadyActiveTitle")}
            </h2>
          </div>
          <p className="text-green-100/80 mt-3">
            {t("billing.connect.alreadyActiveDesc")}
          </p>
          <button
            onClick={() => navigate("/app/settings/payments")}
            className="mt-6 px-4 py-2 rounded-xl bg-white text-black font-medium hover:bg-white/90"
          >
            {t("billing.connect.goToPayments")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">{info?.title}</h1>
          <p className="text-lg sm:text-xl text-white/70">{info?.subtitle}</p>
        </div>

        {/* Error Alert */}
        {error ? (
          <div className="mb-6 rounded-xl border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {(info?.benefits || []).map((benefit, idx) => {
            const IconComponent = iconMap[benefit.icon] || CheckCircle;
            return (
              <div
                key={idx}
                className="rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/8 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <IconComponent className="text-2xl text-white/80" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-sm text-white/70">{benefit.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Requirements */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">{info?.requirements?.title}</h3>
          <ul className="space-y-2">
            {(info?.requirements?.items || []).map((item, idx) => (
              <li key={idx} className="flex items-center gap-3 text-white/80">
                <CheckCircle className="text-green-400 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Fee Info */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-8">
          <h3 className="text-lg font-semibold mb-3">{t("billing.connect.feesTitle")}</h3>
          <p className="text-white/80 mb-4">{info?.fee_info?.description}</p>

          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <div className="text-sm text-white/60 mb-2">{t("billing.connect.feeExample")}</div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-white/80">{t("billing.connect.clientPays")}</span>
                <span className="font-semibold">{info?.fee_info?.example?.client_pays}</span>
              </div>
              <div className="flex justify-between text-green-400">
                <span>{t("billing.connect.youReceive")}</span>
                <span className="font-semibold">{info?.fee_info?.example?.you_receive}</span>
              </div>
              <div className="flex justify-between text-sm text-white/50">
                <span>{t("billing.connect.appointiqFee")}</span>
                <span>{info?.fee_info?.example?.appointiq_fee}</span>
              </div>
              <div className="flex justify-between text-xs text-white/40">
                <span>{t("billing.connect.stripeFee")}</span>
                <span>{info?.fee_info?.example?.stripe_processing}</span>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">{t("billing.connect.faqTitle")}</h3>
          <div className="space-y-3">
            {(info?.faq || []).map((item, idx) => {
              const isExpanded = expandedFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-xl border border-white/10 bg-black/20 overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(isExpanded ? null : idx)}
                    className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-medium">{item.question}</span>
                    {isExpanded ? (
                      <ChevronDown className="flex-shrink-0 text-white/60" />
                    ) : (
                      <ChevronRight className="flex-shrink-0 text-white/60" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 text-sm text-white/70 border-t border-white/10 pt-4">
                      {item.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-6 sm:p-8 text-center">
          <h3 className="text-xl sm:text-2xl font-bold mb-3">
            {info?.cta?.primary}
          </h3>
          <p className="text-white/70 mb-6">{info?.cta?.estimated_time}</p>

          <button
            onClick={handleActivate}
            disabled={activating}
            className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white text-black font-semibold text-lg hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {activating ? t("billing.connect.opening") : info?.cta?.primary}
          </button>

          <div className="mt-4">
            <a
              href="mailto:contact@appointiq.ro"
              className="text-sm text-white/60 hover:text-white underline"
            >
              {info?.cta?.secondary || "Am întrebări (contact support)"}
            </a>
          </div>

          <p className="text-xs text-white/50 mt-4">
            {t("billing.connect.redirectNote")}
            <br />
            {t("billing.connect.redirectNote2")}
          </p>
        </div>

        {/* Back button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate(-1)}
            className="text-sm px-4 py-2 rounded-xl border border-white/15 hover:bg-white/10"
          >
            {t("billing.connect.backBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
